import {type ChangeEvent, type CSSProperties, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {updateMe, uploadMyAvatar, deleteMyAvatar} from '../../common/api/generated/endpoints/auth/auth'
import {getMyHistory, getInvoice} from '../../common/api/generated/endpoints/credits/credits'
import type {CreditLedgerEntry} from '../../common/api/generated/model/creditLedgerEntry'
import {useAuth} from '../auth/useAuth'
import {useMe} from '../../common/hooks/useMe'
import {useToast} from '../../common/components/toast/toastContext'
import {handleApiError} from '../../common/errors/handleApiError'
import {isApiError} from '../../common/errors/ApiError'
import {trackEvent} from '../../common/analytics/trackEvent'
import LoadingLogo from '../components/LoadingLogo'
import PurchaseSuccessModal from '../components/pricing/PurchaseSuccessModal'
import RedeemPromoCodeCard from '../components/account/RedeemPromoCodeCard'
import {computeInitials} from '../components/userAvatar/initials'
import './TemplatesPage.css'
import './AccountPage.css'

type LedgerType = CreditLedgerEntry['type']
type RefundStatus = CreditLedgerEntry['refundStatus']

const PILL_BY_TYPE: Record<LedgerType, 'purchase' | 'reward' | 'spend' | 'refund'> = {
  PURCHASE: 'purchase',
  FORK_REWARD: 'reward',
  RENDER: 'spend',
  ADJUSTMENT: 'reward',
  REFUND: 'refund',
}

const REFUND_WINDOW_DAYS = 14

const refundEligibleUntil = (createdAtIso: string): string => {
  const expiry = new Date(new Date(createdAtIso).getTime() + REFUND_WINDOW_DAYS * 24 * 60 * 60 * 1000)
  return expiry.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric'})
}

const formatDate = (iso: string): string => {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric'})
}

const formatPrice = (cents: number | null | undefined): string | null => {
  if (cents == null) return null
  return `$${(cents / 100).toFixed(2)}`
}

interface IconProps {
  name: 'plus' | 'check' | 'camera' | 'download' | 'signout' | 'spinner'
  size?: number
}
const Icon = ({name, size = 14}: IconProps) => {
  const c = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }
  switch (name) {
    case 'plus':
      return (
        <svg {...c}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      )
    case 'check':
      return (
        <svg {...c}>
          <path d="M5 12l5 5L20 7" />
        </svg>
      )
    case 'camera':
      return (
        <svg {...c}>
          <path d="M4 8h3l1.5-2h7L17 8h3v11H4z" />
          <circle cx="12" cy="13" r="3.5" />
        </svg>
      )
    case 'download':
      return (
        <svg {...c}>
          <path d="M12 4v12" />
          <path d="M7 11l5 5 5-5" />
          <path d="M5 20h14" />
        </svg>
      )
    case 'signout':
      return (
        <svg {...c}>
          <path d="M14 4h4a2 2 0 012 2v12a2 2 0 01-2 2h-4" />
          <path d="M9 12h11" />
          <path d="M16 8l4 4-4 4" />
        </svg>
      )
    case 'spinner':
      return (
        <svg {...c} className="acc-spinner">
          <path d="M21 12a9 9 0 11-9-9" />
        </svg>
      )
    default:
      return null
  }
}

const AccountPage = () => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const {signOut} = useAuth()
  const {me, setMe} = useMe()
  const toast = useToast()

  const [first, setFirst] = useState('')
  const [last, setLast] = useState('')
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileSavedAt, setProfileSavedAt] = useState<number | null>(null)
  const [profileError, setProfileError] = useState<string | null>(null)

  const [avatarBusy, setAvatarBusy] = useState(false)
  const [avatarError, setAvatarError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [history, setHistory] = useState<CreditLedgerEntry[] | null>(null)
  const [historyError, setHistoryError] = useState<string | null>(null)
  const [invoiceBusyId, setInvoiceBusyId] = useState<string | null>(null)

  const [purchaseSuccessOpen, setPurchaseSuccessOpen] = useState(false)
  const [redeemSuccessCredits, setRedeemSuccessCredits] = useState<number | null>(null)

  // LemonSqueezy redirects back to `/account?purchase=success`. Strip the query so the
  // modal doesn't re-fire on refresh, and announce it for analytics.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('purchase') !== 'success') return
    setPurchaseSuccessOpen(true)
    trackEvent('purchase_success_modal_shown')
    navigate(location.pathname, {replace: true})
  }, [location.pathname, location.search, navigate])

  // Seed editable name fields once `me` is available
  useEffect(() => {
    if (!me) return
    setFirst(me.firstName ?? '')
    setLast(me.lastName ?? '')
  }, [me])

  const loadHistory = useCallback(() => {
    getMyHistory({limit: 200})
      .then((res) => setHistory(res.data.entries))
      .catch((err) => setHistoryError(handleApiError(err, 'account.history') ?? t('account.history.loadError')))
  }, [t])

  // Load credit history once
  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  const dirty = useMemo(() => {
    if (!me) return false
    return (first.trim() || null) !== (me.firstName ?? null) || (last.trim() || null) !== (me.lastName ?? null)
  }, [first, last, me])

  const initials = useMemo(() => {
    if (!me) return '?'
    return computeInitials(first || me.firstName, last || me.lastName, me.email)
  }, [first, last, me])

  const avatarGradient = useMemo(() => {
    if (!me) return {av1: '#7C5CFF', av2: '#FF7AB8'}
    return pickGradient(me.id)
  }, [me])

  const handleSaveProfile = async () => {
    if (!me || !dirty) return
    setProfileSaving(true)
    setProfileError(null)
    try {
      const res = await updateMe({
        firstName: first.trim(),
        lastName: last.trim(),
      })
      setMe(res.data)
      trackEvent('account_profile_saved')
      setProfileSavedAt(Date.now())
      window.setTimeout(() => setProfileSavedAt(null), 2200)
    } catch (err) {
      const msg = handleApiError(err, 'account.profile.save') ?? t('account.profile.saveError')
      setProfileError(msg)
      if (isApiError(err) && err.status >= 500) toast.error(msg)
    } finally {
      setProfileSaving(false)
    }
  }

  const handlePickAvatar = () => {
    if (avatarBusy) return
    fileInputRef.current?.click()
  }

  const handleAvatarFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setAvatarError(t('account.avatar.notImage'))
      return
    }
    setAvatarBusy(true)
    setAvatarError(null)
    try {
      const res = await uploadMyAvatar({file})
      setMe(res.data)
      trackEvent('account_avatar_uploaded', {format: file.type, size_bytes: file.size})
    } catch (err) {
      const msg = handleApiError(err, 'account.avatar.upload') ?? t('account.avatar.uploadError')
      setAvatarError(msg)
      if (isApiError(err) && err.status >= 500) toast.error(msg)
    } finally {
      setAvatarBusy(false)
    }
  }

  const handleRemoveAvatar = async () => {
    if (avatarBusy) return
    setAvatarBusy(true)
    setAvatarError(null)
    try {
      const res = await deleteMyAvatar()
      setMe(res.data)
    } catch (err) {
      const msg = handleApiError(err, 'account.avatar.remove') ?? t('account.avatar.removeError')
      setAvatarError(msg)
      if (isApiError(err) && err.status >= 500) toast.error(msg)
    } finally {
      setAvatarBusy(false)
    }
  }

  const handleSignOut = async () => {
    trackEvent('account_signout_clicked')
    await signOut()
    navigate('/login')
  }

  const handlePurchaseSuccessClose = () => setPurchaseSuccessOpen(false)
  const handlePurchaseSuccessPrimary = () => {
    trackEvent('purchase_success_cta_clicked')
    setPurchaseSuccessOpen(false)
    navigate('/editor/new')
  }

  const handleRedeemed = (creditsAdded: number) => {
    setRedeemSuccessCredits(creditsAdded)
    loadHistory()
  }
  const handleRedeemSuccessClose = () => setRedeemSuccessCredits(null)
  const handleRedeemSuccessPrimary = () => {
    setRedeemSuccessCredits(null)
    navigate('/editor/new')
  }

  const handleDownloadInvoice = async (entryId: string) => {
    setInvoiceBusyId(entryId)
    try {
      const res = await getInvoice(entryId)
      window.open(res.data.url, '_blank', 'noopener')
      trackEvent('account_invoice_downloaded', {entry_id: entryId})
    } catch (err) {
      toast.error(handleApiError(err, 'account.invoice') ?? t('account.history.invoiceError'))
    } finally {
      setInvoiceBusyId(null)
    }
  }

  if (!me) {
    return (
      <div className="templates-root account-root">
        <div className="bg-grid" />
        <div className="noise" />
        <main className="projects-page acc-page">
          <div className="acc-loading">
            <LoadingLogo />
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="templates-root account-root">
      <div className="bg-grid" />
      <div className="noise" />

      <main className="projects-page acc-page">
        <header className="mp-head">
          <span className="mp-eyebrow">{t('account.eyebrow')}</span>
          <div className="mp-head-row">
            <div>
              <h1 className="mp-title">
                {t('account.greeting.prefix')} <em>{me.firstName ?? me.email.split('@')[0]}</em>.
              </h1>
              <p className="mp-sub">{t('account.subtitle')}</p>
            </div>
          </div>
        </header>

        <div className="acc-stack">
          <section className="acc-card">
            <header className="acc-card-head">
              <h2>{t('account.profile.title')}</h2>
              <p>{t('account.profile.description')}</p>
            </header>

            <div className="acc-profile-body">
              <div className="acc-avatar-wrap">
                <button
                  type="button"
                  className="acc-avatar"
                  onClick={handlePickAvatar}
                  aria-label={t('account.avatar.uploadLabel')}
                  style={
                    {
                      '--av1': avatarGradient.av1,
                      '--av2': avatarGradient.av2,
                    } as CSSProperties
                  }
                >
                  {me.avatarUrl ? <img src={me.avatarUrl} alt="" /> : <span>{initials}</span>}
                  <span className="acc-avatar-edit">
                    <Icon name="camera" size={14} /> {t('account.avatar.change')}
                  </span>
                </button>
                <div className="acc-avatar-actions">
                  <button type="button" className="acc-avatar-btn" onClick={handlePickAvatar} disabled={avatarBusy}>
                    {me.avatarUrl ? t('account.avatar.replace') : t('account.avatar.upload')}
                  </button>
                  {me.avatarUrl && (
                    <button
                      type="button"
                      className="acc-avatar-btn danger"
                      onClick={handleRemoveAvatar}
                      disabled={avatarBusy}
                    >
                      {t('account.avatar.remove')}
                    </button>
                  )}
                </div>
                {avatarError && <div className="acc-avatar-error">{avatarError}</div>}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  onChange={handleAvatarFile}
                />
              </div>

              <div className="acc-profile-fields">
                <div className="acc-row-2">
                  <div className="acc-field">
                    <label htmlFor="acc-first-name">{t('account.profile.firstName')}</label>
                    <input
                      id="acc-first-name"
                      className="acc-input"
                      value={first}
                      onChange={(e) => setFirst(e.target.value)}
                      autoComplete="given-name"
                    />
                  </div>
                  <div className="acc-field">
                    <label htmlFor="acc-last-name">{t('account.profile.lastName')}</label>
                    <input
                      id="acc-last-name"
                      className="acc-input"
                      value={last}
                      onChange={(e) => setLast(e.target.value)}
                      autoComplete="family-name"
                    />
                  </div>
                </div>
                <div className="acc-field">
                  <label htmlFor="acc-email">{t('account.profile.email')}</label>
                  <input id="acc-email" className="acc-input" value={me.email} readOnly />
                </div>

                <div className="acc-save-row">
                  {profileError && <span className="acc-error-flag">{profileError}</span>}
                  {profileSavedAt && (
                    <span className="acc-saved-flag">
                      <Icon name="check" size={12} /> {t('account.profile.saved')}
                    </span>
                  )}
                  <button
                    type="button"
                    className="acc-btn primary"
                    onClick={handleSaveProfile}
                    disabled={!dirty || profileSaving}
                  >
                    {profileSaving ? t('account.profile.saving') : t('account.profile.save')}
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="acc-balance">
            <div>
              <div className="acc-balance-label">{t('account.balance.label')}</div>
              <div className="acc-balance-value">
                {me.credits}
                <span className="unit">{t('account.balance.unit')}</span>
              </div>
            </div>
            <Link to="/pricing" className="acc-btn primary acc-btn-lg">
              <Icon name="plus" size={13} /> {t('account.balance.buy')}
            </Link>
          </section>

          <RedeemPromoCodeCard me={me} setMe={setMe} onRedeemed={handleRedeemed} />

          <section className="acc-card">
            <header className="acc-card-head">
              <h2>{t('account.history.title')}</h2>
              <p>{t('account.history.description')}</p>
            </header>

            <div className="acc-tx-table">
              <div className="acc-tx-head">
                <div>{t('account.history.col.date')}</div>
                <div>{t('account.history.col.activity')}</div>
                <div className="num">{t('account.history.col.change')}</div>
                <div className="num">{t('account.history.col.balance')}</div>
                <div className="num">{t('account.history.col.invoice')}</div>
              </div>
              {history === null && !historyError && (
                <div className="acc-tx-row">
                  <div style={{gridColumn: '1 / -1', justifyContent: 'center'}}>{t('account.history.loading')}</div>
                </div>
              )}
              {historyError && (
                <div className="acc-tx-row">
                  <div style={{gridColumn: '1 / -1', color: '#fca5a5'}}>{historyError}</div>
                </div>
              )}
              {history && history.length === 0 && (
                <div className="acc-tx-row">
                  <div style={{gridColumn: '1 / -1', justifyContent: 'center'}}>{t('account.history.empty')}</div>
                </div>
              )}
              {history?.map((tx) => {
                const pill = PILL_BY_TYPE[tx.type]
                const sign = tx.amount > 0 ? '+' : ''
                const {title, meta} = describeEntry(tx, t)
                const refundBadge = tx.type === 'PURCHASE' ? renderRefundBadge(tx.refundStatus, tx.createdAt, t) : null
                return (
                  <div className="acc-tx-row" key={tx.id}>
                    <div className="acc-tx-date">{formatDate(tx.createdAt)}</div>
                    <div className="acc-tx-desc">
                      <span className={`acc-tx-type-pill ${pill}`}>{t(`account.history.pill.${pill}`)}</span>
                      <strong>{title}</strong>
                      {meta && <span className="acc-tx-meta">{meta}</span>}
                      {refundBadge}
                    </div>
                    <div className={`acc-tx-amount ${tx.amount > 0 ? 'pos' : 'neg'}`}>
                      {sign}
                      {tx.amount}
                    </div>
                    <div className="acc-tx-balance">{tx.balanceAfter}</div>
                    <div className="acc-tx-action">
                      {tx.type === 'PURCHASE' ? (
                        <button
                          type="button"
                          onClick={() => handleDownloadInvoice(tx.id)}
                          disabled={invoiceBusyId === tx.id}
                          title={t('account.history.invoiceTitle')}
                        >
                          <Icon name={invoiceBusyId === tx.id ? 'spinner' : 'download'} size={12} />{' '}
                          {t('account.history.invoiceLabel')}
                        </button>
                      ) : (
                        <span className="acc-no-invoice">—</span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="acc-card-foot">
              <span>{t('account.history.foot.notice')}</span>
            </div>
          </section>

          <div className="acc-signout-row">
            <div className="acc-signout-text">
              <strong>
                {t('account.signOut.signedInAs')} {me.email}
              </strong>
            </div>
            <button type="button" className="acc-signout-btn" onClick={handleSignOut}>
              <Icon name="signout" size={16} /> {t('account.signOut.button')}
            </button>
          </div>
        </div>
      </main>

      {purchaseSuccessOpen && (
        <PurchaseSuccessModal onPrimary={handlePurchaseSuccessPrimary} onClose={handlePurchaseSuccessClose} />
      )}

      {redeemSuccessCredits !== null && (
        <PurchaseSuccessModal
          eyebrow={t('account.redeemSuccess.eyebrow')}
          title={t('account.redeemSuccess.title', {amount: redeemSuccessCredits})}
          body={t('account.redeemSuccess.body')}
          primaryLabel={t('account.redeemSuccess.ctaPrimary')}
          secondaryLabel={t('account.redeemSuccess.ctaSecondary')}
          onPrimary={handleRedeemSuccessPrimary}
          onClose={handleRedeemSuccessClose}
        />
      )}
    </div>
  )
}

// ── Helpers ────────────────────────────────────────────────────────────

const COLOR_POOL = [
  '#7C5CFF',
  '#A78BFA',
  '#FF7A6B',
  '#FFB088',
  '#86EFAC',
  '#7DD3FC',
  '#F0ABFC',
  '#FACC15',
  '#FB923C',
  '#F472B6',
  '#1E4D3F',
  '#1B5E8C',
]

const hashString = (s: string): number => {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return Math.abs(h)
}

const pickGradient = (seed: string): {av1: string; av2: string} => {
  const h = hashString(seed)
  return {av1: COLOR_POOL[h % COLOR_POOL.length]!, av2: COLOR_POOL[(h * 7 + 3) % COLOR_POOL.length]!}
}

const PURCHASE_LABEL_KEYS: Record<string, string> = {
  small: 'pricing.packs.small.name',
  medium: 'pricing.packs.medium.name',
  large: 'pricing.packs.large.name',
}

type Translator = (key: string, opts?: Record<string, unknown>) => string

const describeEntry = (tx: CreditLedgerEntry, t: Translator): {title: string; meta: string | null} => {
  switch (tx.type) {
    case 'PURCHASE': {
      const labelKey = tx.packageSlug ? PURCHASE_LABEL_KEYS[tx.packageSlug] : null
      const packLabel = labelKey ? t(labelKey) : (tx.packageSlug ?? t('account.history.purchase.title'))
      const title = t('account.history.purchase.title', {pack: packLabel})
      const price = formatPrice(tx.pricePaidCents)
      return {title, meta: price ? t('account.history.purchase.meta', {price}) : null}
    }
    case 'RENDER': {
      const sceneCount = tx.sceneCount ?? 0
      return {
        title: t('account.history.render.title', {count: sceneCount}),
        meta: null,
      }
    }
    case 'FORK_REWARD':
      return {
        title: t('account.history.forkReward.title', {
          project: tx.sourceProjectName ?? t('account.history.forkReward.unknownProject'),
        }),
        meta: null,
      }
    case 'ADJUSTMENT':
      return {title: tx.description ?? t('account.history.adjustment.title'), meta: null}
    case 'REFUND':
      return {title: tx.description ?? t('account.history.refund.title'), meta: null}
    default:
      return {title: tx.description ?? '', meta: null}
  }
}

const renderRefundBadge = (status: RefundStatus, createdAt: string, t: Translator) => {
  if (!status) return null
  if (status === 'eligible') {
    return (
      <span className="acc-refund-badge eligible">
        {t('account.history.refundStatus.eligible', {date: refundEligibleUntil(createdAt)})}
      </span>
    )
  }
  if (status === 'used') {
    return <span className="acc-refund-badge used">{t('account.history.refundStatus.used')}</span>
  }
  if (status === 'expired') {
    return <span className="acc-refund-badge expired">{t('account.history.refundStatus.expired')}</span>
  }
  if (status === 'refunded') {
    return <span className="acc-refund-badge refunded">{t('account.history.refundStatus.refunded')}</span>
  }
  return null
}

export default AccountPage
